import React, { useEffect, useState } from 'react';
import { SearchContainer } from '@components/search/common/container/SearchContainer';
import { Icons } from '@model/common/icons';
import { ThemeProps } from '@theme/base';
import styled from '@emotion/styled';
import { mq } from '@styles/breakpoints';
import { Tabs } from '@components/search/dates/tabs/Tabs';
import { Calendar } from '@components/search/dates/calendar/Calendar';
import { Months } from '@components/search/dates/months/Months';
import { NightsCounter } from '@components/search/dates/duration/NightsCounter';
import { FlexibleDates } from '@components/search/dates/flexible/FlexibleDates';
import { useI18NextContext } from '@components/hooks';
import { ISO_8601_DATE_FORMAT } from '@model/common';
import { Dictionary, groupBy, flatten } from 'lodash';
import moment from 'moment';

interface ContainerProps {
  isMonthOnly?: boolean;
}
const Container = styled.div(({ isMonthOnly }: ContainerProps) => ({
  width: '100%',
  height: '100%',
  [mq.medium]: {
    width: isMonthOnly ? 360 : 680,
    height: 'auto'
  }
}));

const Header = styled.h5(({ theme }: ThemeProps) => ({
  marginTop: 0,
  marginBottom: 0,
  paddingTop: theme.custom.spacing.medium * 2,
  textAlign: 'center'
}));

const SubHeader = styled.p(({ theme }: ThemeProps) => ({
  fontSize: '1.5rem',
  margin: `0 ${theme.custom.spacing.small}px`,
  textAlign: 'center'
}));

export interface DatesProps {
  header?: string;
  subheader?: string;
  value?: string;
  date: string;
  month: string | Array<string>;
  flexibleDays: number;
  tabsRef: any;
  onDateChange: (date: string) => void;
  onMonthChange: (month: string) => void;
  onFlexibleDaysChange: (value: number) => void;
  onClear: () => void;
  isError: boolean;
  isCondensed: boolean;
  isMonthOnly?: boolean;
  placeholderKey?: string;
  isTour?: boolean;
}

export function getMonths(date: moment.Moment): Dictionary<Array<string>> {
  const months: Array<string> = [];
  const dateStart = date.clone().startOf('year');
  const dateEnd = date.clone().add(2, 'year').endOf('year');
  const interim = dateStart.clone();
  while (dateEnd > interim || interim.format('M') === dateEnd.format('M')) {
    months.push(interim.format(ISO_8601_DATE_FORMAT));
    interim.add(1, 'month');
  }
  return groupBy(months, (month: string) => moment(month).year());
}

export const Dates = ({
  header,
  subheader,
  value,
  date,
  month,
  flexibleDays,
  onDateChange,
  onFlexibleDaysChange,
  onMonthChange,
  onClear,
  tabsRef,
  isError,
  isCondensed,
  isMonthOnly,
  placeholderKey,
  isTour
}: DatesProps) => {
  const [tab, setTab] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const t: any = useI18NextContext();
  useEffect(() => {
    if (!isActive) {
      setTab(!month ? 0 : 1);
    }
  }, [isActive, month]);

  // Derive allSelected from month and flatMonths
  const monthsDict = getMonths(moment());
  const flatMonths = flatten(Object.values(monthsDict));
  const monthIsArray = Array.isArray(month);
  const allSelected = monthIsArray && flatMonths.length === month.length;

  const tabItems = [
    ...(!isMonthOnly ? [<Calendar date={date} onChange={onDateChange} key="calendar" />] : []),
    <Months
      month={month}
      onChange={onMonthChange}
      onClear={onClear}
      isMonthOnly={isMonthOnly}
      key="months"
      allSelected={allSelected}
    />
  ];

  return (
    <SearchContainer
      tabsRef={tabsRef}
      title={'search__dates--title'}
      icon={Icons.CALENDAR}
      isCondensed={isCondensed}
      isClearable={!!value}
      onClear={onClear}
      isActive={isActive}
      isError={isError}
      onSetActive={setIsActive}
      hasGradient={false}
      renderError={() => <div>{t('search__dates--placeholder')}</div>}
      renderValue={() => (value ? <div>{value}</div> : t(placeholderKey || 'search__dates--placeholder'))}
      renderBody={() => (
        <Container isMonthOnly={isMonthOnly}>
          {header && <Header>{t(header)}</Header>}
          {subheader && <SubHeader>{t(subheader)}</SubHeader>}
          <Tabs tab={tab} onChange={setTab} isTour={isTour}>
            {tabItems}
          </Tabs>
          {!isTour && <NightsCounter />}
          {tab === 0 && <FlexibleDates flexibleDays={flexibleDays} onFlexibleDaysChange={onFlexibleDaysChange} />}
        </Container>
      )}
    />
  );
};
